import { useEffect, useMemo, type Dispatch, type SetStateAction } from "react";

import "./styles.css";

import { InfoPanel, useTable } from "@king-ict/eupisi-ui/components";
import { Table } from "@king-ict/eupisi-ui/components";
import { Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { FormCard } from "../../../components";
import { useAuth } from "../../../hooks/auth/useAuth";
import { CommitteeTypeEnum } from "../../../models/Committee";
import { CommitteeDecision, Request } from "../../../models/Request";
import { useGetInstitutionCountyCommitteeQuery } from "../../../queries/committeeQuery";
import { HasGotMinimumDecisions } from "../types";
import styles from "./CommitteeDecision.module.scss";
import { getInstitutionCommitteeTableMetadata } from "./constants.table";
import { useCommitteeTabPersistence } from "./hooks";
import { prepareCommitteeDecisions } from "./utils";

interface IProps {
  item: Request;
  committeeDecisions: CommitteeDecision[];
  committeeType: CommitteeTypeEnum;
  id: number;
  setHasGotMinimumDecisions: Dispatch<SetStateAction<HasGotMinimumDecisions>>;
  onSaveOuterState: () => void;
}

export const CommitteeDecisionWrapper = ({
  item,
  committeeDecisions,
  committeeType,
  id,
  setHasGotMinimumDecisions,
  onSaveOuterState,
}: IProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();

  const {
    data: committeeResponse,
    isLoading,
    error,
    isFetching,
    refetch: refetchCommitteeResponse,
  } = useGetInstitutionCountyCommitteeQuery(id);

  const committees = useMemo(
    () =>
      committeeType === CommitteeTypeEnum.Institution
        ? committeeResponse?.institutionCommittees
        : committeeResponse?.countyCommittees,
    [committeeType, committeeResponse],
  );

  const { activeTabKey, tabConfigs, handleTabChange, onBeforeNavigate } = useCommitteeTabPersistence(
    committees,
    onSaveOuterState,
  );

  useEffect(() => {
    if (!committees?.length || !committeeResponse) return;

    setHasGotMinimumDecisions({
      county: committeeResponse.hasCountyWithMinDecisionNumber,
      institution: committeeResponse.hasInstitutionWithMinDecisionNumber,
    });
  }, [committeeResponse, committeeType, committees, setHasGotMinimumDecisions]);

  useEffect(() => {
    refetchCommitteeResponse();
  }, [history.location.pathname]);

  const tableMetadata = useMemo(
    () =>
      getInstitutionCommitteeTableMetadata({
        t,
        committeeDecisions: prepareCommitteeDecisions(committeeDecisions),
        item,
        history,
        auth,
        committeeType,
        committees,
        onBeforeNavigate,
      }),
    [t, committeeDecisions, item, history, auth, committeeType, committees, onBeforeNavigate],
  );

  const activeTab = useMemo(() => tabConfigs.find((tab) => tab.key === activeTabKey), [tabConfigs, activeTabKey]);

  const currentEmployees = useMemo(() => {
    return activeTab?.employees || [];
  }, [activeTab]);

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: currentEmployees,
    totalItems: currentEmployees.length,
  });

  const renderTabContent = () => {
    if (isLoading || isFetching) {
      return (
        <FormCard>
          <div>Loading...</div>
        </FormCard>
      );
    }

    if (error) {
      return (
        <FormCard>
          <InfoPanel color="danger" fullWidth message={t("common.error.loading")} />
        </FormCard>
      );
    }

    if (currentEmployees.length) {
      return (
        <FormCard>
          <Table {...tableProps} />
        </FormCard>
      );
    }

    return <NoCommitteeError t={t} />;
  };

  if (!tabConfigs.length) return <NoCommitteeError t={t} />;

  const tabsAriaLabel =
    committeeType === CommitteeTypeEnum.Institution
      ? "institutionCommitteeDecisionInfo"
      : "countyCommitteeDecisionInfo";

  // Make the active one colorful
  return (
    <FormCard>
      <Tabs
        id="committee-decision-tabs"
        aria-label={t(`common.requestModel.requestTabs.${tabsAriaLabel}`)}
        activeKey={activeTabKey || undefined}
        onSelect={handleTabChange}
        className={styles.dateTabs}
      >
        {tabConfigs.map((tab) => (
          <Tab
            key={tab.key}
            eventKey={tab.key}
            title={tab.title}
            tabClassName={`${activeTabKey === tab.key ? styles.activeTab : ""} ${
              tab.isActive ? styles.activeCommittee : styles.inactiveCommittee
            }`}
            aria-selected={activeTabKey === tab.key}
            aria-controls={`panel-${tab.key}`}
          >
            {renderTabContent()}
          </Tab>
        ))}
      </Tabs>
    </FormCard>
  );
};

function NoCommitteeError({ t }: { t: (key: string) => string }) {
  return (
    <FormCard>
      <InfoPanel color="danger" fullWidth message={t("request.errorMessage.noCommittee")} />
    </FormCard>
  );
}
